import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, combineLatest, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	DeviceAuthenticationStatusService,
	mapDeviceAuthenticationFromSysApi
} from '@shure/shared/angular/data-access/device-authentication';
import {
	ApolloQueryError,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	NodeChangeType,
	SubscriptionManagerService
} from '@shure/shared/angular/data-access/system-api/core';
import {
	mapChannelGroupFromSysApi,
	mapDeviceModelFromSysApi,
	mapDeviceConferencingOperationModeFromSysApi,
	mapOutputModeFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { EthernetPowerSourceType, Response, ResponseState } from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { BypassDeviceChannel, BypassDspDevice } from '../../api/models';
import { BypassDspApiService } from '../../api/services';
import {
	BypassAllEqMutationGQL,
	BypassDspDeviceFragment,
	BypassDspDeviceQueryGQL,
	BypassDspDeviceSubscriptionGQL,
	BypassIntellimixMutationGQL
} from '../graphql/generated/bypass-dsp.generated';

@Injectable()
export class DaiBypassDspService implements BypassDspApiService {
	private readonly logger: ILogger;

	private readonly bypassDspDeviceSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly bypassDspDeviceQueryGQL: BypassDspDeviceQueryGQL,
		private readonly bypassIntellimixMutationGQL: BypassIntellimixMutationGQL,
		private readonly bypassAllEqMutationGQL: BypassAllEqMutationGQL,
		private readonly deviceAuthenticationStatusService: DeviceAuthenticationStatusService,
		bypassDspDeviceSubscriptionGQL: BypassDspDeviceSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiBypassDspService');

		this.bypassDspDeviceSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiBypassDspService',
				changeTypes: [
					NodeChangeType.DeviceAllIntellimixBypass,
					NodeChangeType.DeviceAllEqualizersBypass,
					NodeChangeType.DeviceAudioChannels,
					NodeChangeType.DeviceAuthentication,
					NodeChangeType.DeviceAnalogAudioOutputMode
				],
				subscriptionGql: bypassDspDeviceSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getDevice$(deviceId: string): Observable<BypassDspDevice> {
		const queryId = uuidv4();
		this.logger.trace('getDevice$()', 'Querying bypass dsp device', { queryId: queryId, deviceId: deviceId });

		return combineLatest([
			this.bypassDspDeviceQueryGQL.watch({ deviceId }, { errorPolicy: 'all' }).valueChanges,
			this.deviceAuthenticationStatusService.getDeviceState$(deviceId)
		]).pipe(
			map(([query, auth]) => {
				if (query.data?.node && 'features' in query.data.node) {
					this.logger.trace('getDevice$()', 'Query updated', { queryId: queryId, deviceId: deviceId });
					return this.mapBypassDspDeviceFromSysApi(query.data.node, auth.authenticated, auth.readonlyMode);
				}

				this.logger.error('getDevice$()', 'Failed to query bypass dsp device', {
					queryId: queryId,
					deviceId: deviceId
				});
				throw ApolloQueryErrorMapper.getError(query);
			}),
			tap((device) => this.bypassDspDeviceSubscriptionManager.subscribe(queryId, device.id)),
			noMoreSubscribers(() => {
				this.logger.trace('getDevice$', 'noMoreSubscribers', { queryId: queryId, deviceId: deviceId });
				this.bypassDspDeviceSubscriptionManager.unsubscribeQuery(queryId);
			}),
			catchError((error) => {
				return throwError(() => new ApolloQueryError(error));
			})
		);
	}

	public bypassIntellimix(deviceId: string, bypassed: boolean): Observable<Response<void, string>> {
		this.logger.trace('bypassIntellimix()', 'Toggling bypass intellimix', {
			deviceId: deviceId,
			bypassed: bypassed
		});
		return this.bypassIntellimixMutationGQL
			.mutate(
				{
					updates: [
						{
							device: {
								id: deviceId,
								features: {
									allIntellimixBypass: {
										bypassed: bypassed
									}
								}
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('bypassIntellimix()', 'Failed to toggle bypass intellimix', {
							deviceId: deviceId,
							bypassed: bypassed,
							result: result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('bypassIntellimix()', 'bypass intellimix successful', {
						deviceId: deviceId,
						bypassed: bypassed
					});
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('bypassIntellimix()', 'Error', {
						error: error,
						deviceId: deviceId,
						bypassed: bypassed
					});
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	public bypassAllEq(deviceId: string, bypassed: boolean): Observable<Response<void, string>> {
		this.logger.trace('bypassAllEq()', 'Toggling bypass all eq', {
			deviceId: deviceId,
			bypassed: bypassed
		});
		return this.bypassAllEqMutationGQL
			.mutate(
				{
					updates: [
						{
							device: {
								id: deviceId,
								features: {
									allEqualizersBypass: {
										bypassed: bypassed
									}
								}
							}
						}
					],
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((result) => {
					if (
						result.data?.updateNodes &&
						'error' in result.data.updateNodes[0] &&
						result.data.updateNodes[0].error !== null
					) {
						this.logger.error('bypassAllEq()', 'Failed to toggle bypass all eq', {
							deviceId: deviceId,
							bypassed: bypassed,
							result: result
						});
						return { state: ResponseState.Error, error: result.data.updateNodes[0].error?.message };
					}

					this.logger.trace('bypassAllEq()', 'bypass all eq successful', {
						deviceId: deviceId,
						bypassed: bypassed
					});
					return { state: ResponseState.Done };
				}),
				catchError((error: Error) => {
					this.logger.error('bypassAllEq()', 'Error', {
						error: error,
						deviceId: deviceId,
						bypassed: bypassed
					});
					return of({ state: ResponseState.Error, error: error.message });
				})
			);
	}

	private mapBypassDspDeviceFromSysApi(
		device: BypassDspDeviceFragment,
		authenticated: boolean | false,
		readonlyModeSelected: boolean | false
	): BypassDspDevice {
		const isBypassDsp =
			!device.description.features.allEqualizersBypass.supported &&
			device.description.features.allIntellimixBypass.supported;

		return {
			id: device.id,
			virtual: device.virtual,
			hardwareId: device.hardwareId,
			model: mapDeviceModelFromSysApi(device.description.model),
			readonly: readonlyModeSelected,
			bypassEqualizer: isBypassDsp
				? device.features.allIntellimixBypass?.bypassed ?? false
				: device.features.allEqualizersBypass?.bypassed ?? false,
			bypassIntellimix: device.features.allIntellimixBypass?.bypassed ?? false,
			isBypassDsp: isBypassDsp,
			features: {
				audioChannels:
					device.features.audioChannels?.audioChannels.map(
						(fragment) =>
							<BypassDeviceChannel>{
								id: fragment.id,
								group: mapChannelGroupFromSysApi(fragment.description.groupName),
								features: {
									name: fragment.features.name?.name ?? ''
								}
							}
					) ?? [],
				allIntellimixBypass: {
					bypassed: device.features.allIntellimixBypass?.bypassed ?? false
				},
				allEqualizersBypass: {
					bypassed: isBypassDsp
						? device.features.allIntellimixBypass?.bypassed ?? false
						: device.features.allEqualizersBypass?.bypassed ?? false
				},
				firmware: {
					version: device.features.firmware?.version || ''
				},
				ethernetPowerSource: {
					type: device.features.ethernetPowerSource?.type ?? EthernetPowerSourceType.Unknown
				},
				conferencingOperationMode: device.description.features.conferencingOperationMode.supported
					? {
							mode: mapDeviceConferencingOperationModeFromSysApi(
								device.features.conferencingOperationMode?.mode
							),
							isMissing: !device.features.conferencingOperationMode
					  }
					: undefined,
				analogAudioOutputMode: device.description.features.analogAudioOutputMode.supported
					? {
							mode: mapOutputModeFromSysApi(device.features.analogAudioOutputMode?.mode),
							isMissing: !device.features.analogAudioOutputMode
					  }
					: undefined,
				authentication: mapDeviceAuthenticationFromSysApi(
					device.features.authentication,
					authenticated,
					readonlyModeSelected
				)
			}
		};
	}
}
